import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, map, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';



export interface FeedBackQuestionApi {
  questionId: number;
  question: string;
  teluguQuestion: string;
  questionType: 'SINGLE' | 'MULTIPLE';
}

/** Question with nested options from feedBackQuestionAndOptions API. */
export interface FeedBackQuestionAndOptionApi {
  questionId: number;
  question: string;
  teluguQuestion: string;
  questionType: 'SINGLE' | 'MULTIPLE';
  questionTitle?: string;
  options: FeedBackOptionApi[];
}

export interface FeedBackOptionApi {
  optionId: number;
  optionName: string;
  teluguOptionName: string;
  optionRank: number;
  optionRankStatus: boolean;
}

/** One option in feedback history (previous stored details). */
export interface FeedbackHistoryOption {
  optionId: number;
  feedbackId: number;
  status: boolean;
  feedbackOptionText: string;
  feedbackPopUpText: string;
  option?: string;
  optionRank?: number;
}

/** One question's options in feedback history. */
export interface FeedbackHistoryQuestionOption {
  questionId: number;
  question?: string;
  options: FeedbackHistoryOption[];
}

/** One entry from feedback history API (e.g. one submission date). */
export interface FeedbackHistoryEntry {
  feedBackDate: string;
  questionAndOption: FeedbackHistoryQuestionOption[];
}

/** Response from studentFeedback/feedbackDetails – used to pre-fill the form. */
export interface FeedbackDetailsResponse {
  admNO: string;
  sectionId: number;
  questionAndOption: FeedbackHistoryQuestionOption[];
  feedBackType: string;
  validateMessage: string;
}

/** POST /college/studentSearch request body. */
export interface CollegeStudentSearchPayload {
  stdName: string;
  admNo: number | string;
  parentName: string;
  mobileNo: number;
  pageCount: number;
  searchKey: string;
  businessType: string;
}

/** Student row from college/studentSearch. */
export interface StudentSearchResult {
  admNo?: string;
  name?: string;
  studentName?: string;
  mobileNo?: string | number;
  uniqueRefId?: number | string;
  stdUniqueRefId?: number | string;
  searchSelected?: string;
  sectionId?: number;
  campus?: string;
  campusName?: string;
  sectionName?: string;
  courseTrackName?: string;
  studentType?: string;
  parentName?: string;
  surname?: string;
  status?: string;
  businessType?: string;
}

/** Recent search row from searchCacheDetails. */
export interface StudentSearchCacheItem {
  searchKey: string;
  searchName: string;
  searchSelected: string;
  searchType: string;
  parentName?: string;
  favourite?: boolean;
  businessType?: string;
  stdUniqueRefId?: string;
}

export interface EmployeeState {
  stateId: number;
  stateName: string;
}

export interface EmployeeCity {
  cityId: number;
  cityName: string;
}

export interface EmployeeCampus {
  campusId: number;
  campusName: string;
}

/** One row from studentWellBeingReport API. */
export interface StudentWellBeingReportRow {
  feedBackDate: string;
  admNo: string;
  studentName?: string;
  sectionName?: string;
  campusName?: string;
  questionAndOption: FeedbackHistoryQuestionOption[];
}

/** Paginated studentWellBeingReport API response. */
export interface StudentWellBeingReportPageResult {
  rows: StudentWellBeingReportRow[];
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

/** Submit payload for student feedback. */
export interface FeedbackSubmitPayload {
  admNO: string;
  category: string;
  empId: number;
  sectionId: number;
  questionAndOption: {
    questionId: number;
    options: {
      optionId: number;
      feedbackId: number;
      status: boolean;
      feedbackOptionText: string;
      feedbackPopUpText: string;
    }[];
  }[];
  feedBackType: string;
  validateMessage: string;
}



@Injectable({
  providedIn: 'root'
})
export class StudentwellbeingService {
  private readonly baseUrl = environment.masterDomain;
  private token = '';

  private get headers(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `${this.token}`,
    });
  }

  private get jsonHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `${this.token}`,
      'Content-Type': 'application/json',
    });
  }

  constructor(private http: HttpClient) { }



  /** Build POST body from adm no, mobile, or student name search key. */
  buildCollegeStudentSearchPayload(searchKey: string): CollegeStudentSearchPayload {
    const key = String(searchKey ?? '').trim();
    const payload: CollegeStudentSearchPayload = {
      stdName: '',
      admNo: 0,
      parentName: '',
      mobileNo: 0,
      pageCount: 0,
      searchKey: key,
      businessType: 'COLLEGE',
    };
    if (!key) {
      return payload;
    }
    if (/^\d+$/.test(key)) {
      const num = Number(key);
      if (key.length === 10 && /^[6-9]/.test(key)) {
        payload.mobileNo = num;
      } else {
        payload.admNo = num;
      }
    } else {
      payload.stdName = key;
    }
    return payload;
  }

  private mapCollegeStudentRow(row: Record<string, unknown>): StudentSearchResult {
    const name = String(row['name'] ?? '').trim();
    return {
      admNo: String(row['admNo'] ?? '').trim(),
      name,
      studentName: name,
      mobileNo: row['mobileNo'] as string | number | undefined,
      uniqueRefId: row['stdUniqueRefId'] as string | undefined,
      stdUniqueRefId: row['stdUniqueRefId'] as string | undefined,
      searchSelected: String(row['admNo'] ?? '').trim(),
      campus: String(row['campusName'] ?? '').trim(),
      campusName: String(row['campusName'] ?? '').trim(),
      sectionName: String(row['sectionName'] ?? '').trim(),
      courseTrackName: String(row['courseTrackName'] ?? '').trim(),
      studentType: String(row['studentType'] ?? '').trim(),
      parentName: String(row['parentName'] ?? '').trim(),
      surname: String(row['surname'] ?? '').trim(),
      status: String(row['status'] ?? '').trim(),
      businessType: String(row['businessType'] ?? '').trim(),
    };
  }

  studentSearch(searchCriteria: string): Observable<StudentSearchResult[]> {
    const payload = this.buildCollegeStudentSearchPayload(searchCriteria);
    const url = `${this.baseUrl}/employee/api/jwtV2/college/studentSearch`;
    return this.http.post<Record<string, unknown>[]>(url, payload, { headers: this.jsonHeaders }).pipe(
      map((res) => {
        const rows = Array.isArray(res) ? res : [];
        return rows.map((r) => this.mapCollegeStudentRow(r));
      }),
    );
  }

  searchCacheDetails(searchType = 'STUDENT'): Observable<StudentSearchCacheItem[]> {
    const url =
      `${this.baseUrl}/employee/api/jwtV2/student/search/searchCacheDetails?searchType=${encodeURIComponent(searchType)}`;
    return this.http.get<StudentSearchCacheItem[]>(url, { headers: this.headers }).pipe(
      map((res) => (Array.isArray(res) ? res : [])),
    );
  }

  /** POST saveCache — records student picked from search / recent list. */
  saveSearchCache(params: {
    searchKey: string;
    searchSelected: string;
    searchType?: string;
    favourite?: boolean;
    businessType?: string;
    parentName?: string;
  }): Observable<unknown> {
    const httpParams = new HttpParams()
      .set('searchKey', String(params.searchKey ?? '').trim())
      .set('searchSelected', String(params.searchSelected ?? '').trim())
      .set('searchType', params.searchType ?? 'Student')
      .set('favourite', String(params.favourite ?? false))
      .set('businessType', params.businessType ?? 'COLLEGE')
      .set('parentName', String(params.parentName ?? '').trim());
    const url = `${this.baseUrl}/employee/api/jwtV2/student/search/saveCache`;
    return this.http.post(url, {}, { headers: this.headers, params: httpParams });
  }

  /**
   * GET feedBackQuestionAndOptions?categoryName=...&feedBackType=...
   */
  getFeedBackQuestionAndOptions(
    categoryName: string,
    feedBackType = 'STUDENT_FEEDBACK',
  ): Observable<FeedBackQuestionAndOptionApi[]> {
    const url =
      `${this.baseUrl}/employee/api/jwtV2/feedBackQuestionAndOptions` +
      `?categoryName=${encodeURIComponent(categoryName)}` +
      `&feedBackType=${encodeURIComponent(feedBackType)}`;
    return this.http.get<FeedBackQuestionAndOptionApi[]>(url, { headers: this.headers }).pipe(
      map((res) => (Array.isArray(res) ? res : [])),
    );
  }

  /**
 * GET feedBackQuestions?categoryName=...
 */
  getQuestions(categoryName: string): Observable<FeedBackQuestionApi[]> {
    const url = `${this.baseUrl}/employee/api/jwtV2/studentFeedback/feedBackQuestions?categoryName=${encodeURIComponent(categoryName)}`;
    return this.http.get<FeedBackQuestionApi[]>(url, { headers: this.headers });
  }

  /**
  * GET feedBackOptions?questionId=...
  */
  getOptions(questionId: number): Observable<FeedBackOptionApi[]> {
    const url = `${this.baseUrl}/employee/api/jwtV2/feedBackOptions?questionId=${questionId}`;
    return this.http.get<FeedBackOptionApi[]>(url, { headers: this.headers });
  }

  /**
  * GET studentFeedback/feedbackHistory?category=...
  * Returns previously stored feedback (list by date).
  */
  getFeedbackHistory(admNo:any, category: string): Observable<FeedbackHistoryEntry[]> {
    const url = `${this.baseUrl}/employee/api/jwtV2/studentFeedback/feedbackHistory?admNo=${encodeURIComponent(admNo)}&category=${encodeURIComponent(category)}`;
    return this.http.get<FeedbackHistoryEntry[]>(url, { headers: this.headers });
  }

  /**
  * GET studentFeedback/feedbackDetails?category=...
  * Returns current student feedback details to pre-fill the form (admNO, sectionId, questionAndOption).
  * May return null if no details exist.
  */
  getFeedbackDetails(admNo: string, category: string): Observable<FeedbackDetailsResponse | null> {
    const url = `${this.baseUrl}/employee/api/jwtV2/studentFeedback/feedbackDetails?admNo=${admNo}&category=${encodeURIComponent(category)}`;
    return this.http.get<FeedbackDetailsResponse | null>(url, { headers: this.headers });
  }

  /**
   * GET student/getUploadImage?admNo=...
   * Returns student image response (200 with body/url, 204 without content).
   */
  getStudentUploadImage(admNo: string): Observable<HttpResponse<Blob>> {
    const url = `${this.baseUrl}/employee/api/jwtV2/student/getUploadImage?admNo=${encodeURIComponent(admNo)}`;
    return this.http.get(url, {
      headers: this.headers,
      observe: 'response',
      responseType: 'blob'
    });
  }

  getEmployeeStates(): Observable<EmployeeState[]> {
    const url = `${this.baseUrl}/apiV2/common/employeeStates`;
    return this.http.get<EmployeeState[]>(url, { headers: this.headers });
  }

  getEmployeeCities(stateId: number): Observable<EmployeeCity[]> {
    const url = `${this.baseUrl}/apiV2/common/emplStateCities?stateId=${stateId}`;
    return this.http.get<EmployeeCity[]>(url, { headers: this.headers });
  }

  getEmployeeCampuses(cityId: number): Observable<EmployeeCampus[]> {
    const url = `${this.baseUrl}/apiV2/common/empCityCampuses?cityId=${cityId}`;
    return this.http.get<EmployeeCampus[]>(url, { headers: this.headers });
  }

  getRecentFeedBackReport(category: string): Observable<StudentWellBeingReportRow[]> {
    const url =
      `${this.baseUrl}/employee/api/jwtV2/empFeedBack/getRecentFeedBackReport` +
      `?category=${encodeURIComponent(category)}`;
    return this.http.get<StudentWellBeingReportRow[]>(url, { headers: this.headers });
  }

  getStudentWellBeingReport(
    stateId: number | null | undefined,
    category: string,
    cityId?: number | null,
    campusId?: number | null,
    fromDate?: string,
    toDate?: string,
    page = 0,
    pageSize = 50,
  ): Observable<StudentWellBeingReportPageResult> {
    let url =
      `${this.baseUrl}/employee/api/jwtV2/studentFeedback/studentWellBeingReport` +
      `?category=${encodeURIComponent(category)}` +
      `&page=${page}` +
      `&pageSize=${pageSize}`;
    if (stateId != null) {
      url += `&stateId=${stateId}`;
    }
    if (cityId != null) {
      url += `&cityId=${cityId}`;
    }
    if (campusId != null) {
      url += `&campusId=${campusId}`;
    }
    if (fromDate) {
      url += `&fromDate=${encodeURIComponent(fromDate)}`;
    }
    if (toDate) {
      url += `&toDate=${encodeURIComponent(toDate)}`;
    }
    return this.http.get<unknown>(url, { headers: this.headers }).pipe(
      map((res) => this.parseStudentWellBeingReportResponse(res, pageSize, page)),
    );
  }

  private parseStudentWellBeingReportResponse(
    res: unknown,
    pageSize: number,
    page: number,
  ): StudentWellBeingReportPageResult {
    if (Array.isArray(res)) {
      const rows = res as StudentWellBeingReportRow[];
      return {
        rows,
        totalRecords: rows.length,
        totalPages: rows.length > 0 ? 1 : 0,
        currentPage: page,
        pageSize,
      };
    }
    if (res && typeof res === 'object') {
      const obj = res as Record<string, unknown>;
      const rawRows = obj['data'] ?? obj['content'] ?? obj['records'] ?? obj['list'] ?? obj['rows'];
      const rows = Array.isArray(rawRows) ? (rawRows as StudentWellBeingReportRow[]) : [];
      const totalRecords = Number(obj['totalRecords'] ?? rows.length);
      const safeTotalRecords = Number.isFinite(totalRecords) && totalRecords >= 0 ? totalRecords : rows.length;
      const totalPages = Number(obj['totalPages'] ?? (safeTotalRecords > 0 ? Math.ceil(safeTotalRecords / pageSize) : 0));
      const currentPage = Number(obj['currentPage'] ?? page);
      const pageSizeFromApi = Number(obj['pageSize'] ?? pageSize);
      return {
        rows,
        totalRecords: safeTotalRecords,
        totalPages: Number.isFinite(totalPages) && totalPages >= 0 ? totalPages : 0,
        currentPage: Number.isFinite(currentPage) ? currentPage : page,
        pageSize: Number.isFinite(pageSizeFromApi) && pageSizeFromApi > 0 ? pageSizeFromApi : pageSize,
      };
    }
    return {
      rows: [],
      totalRecords: 0,
      totalPages: 0,
      currentPage: page,
      pageSize,
    };
  }

  /**
   * POST studentFeedback – submit feedback after changes.
   */
  submitFeedback(payload: FeedbackSubmitPayload): Observable<unknown> {
    const url = `${this.baseUrl}/employee/api/jwtV2/studentFeedback/saveStdFeedbackDetails`;
    return this.http.post(url, payload, { headers: this.headers });
  }

  /**
   * Load questions and options for each question in one flow.
   * Options with optionName "TEXT" get isText: true (show textarea, 1000 words).
   */
  getQuestionsWithOptions(categoryName: string): Observable<{
    id: string;
    text: string;
    textTe: string;
    questionType: 'SINGLE' | 'MULTIPLE';
    options: { id: string; label: string; labelTe: string; isText?: boolean, optionRank: number }[];
  }[]> {
    return this.getQuestions(categoryName).pipe(
      switchMap(questions =>
        forkJoin(
          questions.map(q =>
            this.getOptions(q.questionId).pipe(
              map(opts =>
                opts
                  .sort((a, b) => a.optionRank - b.optionRank)
                  .map(o => ({
                    id: String(o.optionId),
                    label: o.optionName,
                    labelTe: o.teluguOptionName || o.optionName,
                    isText: (o.optionName || '').toUpperCase() === 'TEXT',
                    optionRank: o.optionRank
                  }))
              ),
              map(optionList => ({
                id: String(q.questionId),
                text: q.question,
                textTe: q.teluguQuestion || q.question,
                questionType: q.questionType,
                options: optionList
              }))
            )
          )
        )
      )
    );
  }


}
